import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { STYLE_PROFILES } from '@gilding/shared';
import { api } from '../api/client.js';
import { AddressBadge } from '../components/Identicon.js';
import { Reveal } from '../components/Reveal.js';
import { GildedImage } from '../components/GildedImage.js';

// -----------------------------------------------------------------------------
// Inline SVG ornaments — kept local to this page so the refactor doesn't add
// new exported components. Future cleanup can lift them into components/icons/.
// -----------------------------------------------------------------------------

/** A small laurel-fragment glyph used as the bullet for the "what you'll get"
 *  list. Two leaves curving off a short stem. Sized 12px, accent color. */
function LaurelBullet() {
  return (
    <svg
      viewBox="0 0 14 14"
      width="12"
      height="12"
      aria-hidden="true"
      className="mt-[0.4rem] shrink-0 text-accent"
    >
      <g fill="currentColor">
        <path d="M2 7 C 3.6 5.2, 5.6 4.4, 7 4.8 C 6 6, 4.5 6.8, 2 7 Z" opacity="0.95" />
        <path
          d="M2 7 C 3.6 8.8, 5.6 9.6, 7 9.2 C 6 8, 4.5 7.2, 2 7 Z"
          opacity="0.7"
        />
      </g>
      <path d="M7 7 L11 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

/** A quill resting on a parchment fragment — used in the unavailable state. */
function QuillRest() {
  return (
    <svg
      viewBox="0 0 120 120"
      width="120"
      height="120"
      aria-hidden="true"
      className="text-ink-soft"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <path d="M14 84 L100 84 L106 90 L20 90 Z" opacity="0.6" />
        <path d="M30 84 L30 78 M44 84 L44 78 M58 84 L58 78 M72 84 L72 78 M86 84 L86 78" opacity="0.4" />
      </g>
      <g fill="rgb(var(--color-accent) / 0.85)" stroke="rgb(var(--color-accent-deep))" strokeWidth="0.5">
        {/* Quill body curving over the parchment */}
        <path d="M24 78 Q 50 30, 96 18 Q 80 56, 40 80 Z" />
      </g>
      <line
        x1="36"
        y1="74"
        x2="78"
        y2="42"
        stroke="rgb(var(--color-accent-deep))"
        strokeWidth="0.7"
        opacity="0.55"
      />
    </svg>
  );
}

/* Identicon + attribution moved to components/Identicon.tsx (AddressBadge). */

// -----------------------------------------------------------------------------

export function Remix() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });
  const genQuery = useQuery({
    queryKey: ['gen', id],
    queryFn: () => api.generation(id!),
    enabled: !!id,
    retry: false,
  });

  if (!id) return <Navigate to="/feed" replace />;

  if (genQuery.isLoading || meQuery.isLoading) {
    return (
      <p className="font-display italic text-ink-subtle">Pulling the source plate&hellip;</p>
    );
  }

  if (genQuery.error || !genQuery.data) {
    return (
      <section className="flex flex-col items-center gap-5 py-12 text-center">
        <QuillRest />
        <div className="flex flex-col gap-2">
          <h1 className="heading-section">This gilding has been put away</h1>
          <p className="max-w-sm text-ink-soft">
            The page was unpublished, or the link doesn&apos;t lead anywhere. The feed
            has plenty more to remix.
          </p>
        </div>
        <Link to="/feed" className="btn-primary">
          Back to the feed
        </Link>
      </section>
    );
  }

  const data = genQuery.data;
  // Owner reads see the full payload; non-owners see the redacted card.
  const card =
    data.kind === 'full'
      ? {
          id: data.result.id,
          thumbnail:
            data.result.images[data.result.coverIndex ?? 0]?.url ??
            data.result.images[0]?.url ??
            '',
          stylePreset: data.result.request.stylePreset,
          aspectRatio: data.result.request.aspectRatio,
          ownerAddress: data.result.ownerAddress,
          upvotes: data.upvotes,
          remixes: data.remixes,
        }
      : data.card;
  const isAuthed = meQuery.data?.authenticated ?? false;
  const styleLabel = STYLE_PROFILES[card.stylePreset]?.label ?? 'Untitled style';

  const startRemix = (): void => {
    if (!isAuthed) {
      navigate('/', { state: { redirectAfterLogin: `/remix/${id}` } });
      return;
    }
    navigate(
      `/new?remix=${encodeURIComponent(card.id)}&style=${encodeURIComponent(
        card.stylePreset,
      )}&aspect=${encodeURIComponent(card.aspectRatio)}`,
    );
  };

  // ---------------------------------------------------------------------------
  // Layout — asymmetric 60/40. Image is the hero (left, museum plate),
  // attribution + laurel list ride the right rail. The CTA is overlaid on the
  // image's bottom-right corner as a foil-stamped seal. Mobile stacks; the
  // overlay collapses to a full-width primary button below the plate.
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-12">
      <Reveal as="header" className="flex flex-col gap-3">
        <h1 className="heading-display">Print this scene, with your character</h1>
        <p className="max-w-prose text-lede text-ink-soft">
          The mood, composition, and lighting carry over. The figure becomes yours.
          The amplified prompt is rewritten for the NFT you pick, and you&apos;ll see
          the rewrite the moment your image lands.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-[3fr_2fr] md:items-start md:gap-10">
        {/* ---- Hero: museum plate with overlaid foil-stamp CTA ---- */}
        <Reveal as="figure" delay={110} className="flex flex-col gap-3">
          <div className="card-plate overflow-hidden">
            <GildedImage
              src={card.thumbnail}
              alt="Source gilding"
              className="block w-full"
            />
          </div>

          {/* Tight figcaption — italic display serif, not uppercase-tracked. */}
          <figcaption className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
            <span className="tag">
              <span className="tag-dot" />
              {styleLabel}
            </span>
            <span className="hairline-v h-3" />
            <AddressBadge address={card.ownerAddress} />
          </figcaption>
        </Reveal>

        {/* ---- Right rail: laurel list + secondary actions ---- */}
        <Reveal as="aside" delay={220} className="flex flex-col gap-8">
          <section className="flex flex-col gap-4">
            <h2 className="heading-section">What carries over</h2>
            <ul className="flex flex-col gap-3 text-body text-ink-soft">
              <li className="flex items-start gap-3 font-display italic">
                <LaurelBullet />
                <span>Same scene, mood, and composition as the source plate.</span>
              </li>
              <li className="flex items-start gap-3 font-display italic">
                <LaurelBullet />
                <span>
                  Your character, any NFT you own, rendered with the right
                  anatomy for its species.
                </span>
              </li>
              <li className="flex items-start gap-3 font-display italic">
                <LaurelBullet />
                <span>
                  An amplified prompt rewritten by Claude for your character, visible
                  only to you once the image lands.
                </span>
              </li>
              <li className="flex items-start gap-3 font-display italic">
                <LaurelBullet />
                <span>
                  The original maker&apos;s sketch stays with them. It is never
                  shown.
                </span>
              </li>
            </ul>
          </section>

          {/* Primary CTA — lives in the rail on every size, in the open space
              below the list, so it never overlaps the source image. */}
          <button
            type="button"
            className="btn-primary w-full"
            onClick={startRemix}
          >
            {isAuthed ? 'Remix with my NFT' : 'Connect wallet to remix'}
          </button>

          <div className="flex items-center gap-3">
            <Link to="/feed" className="btn-ghost">
              Back to the feed
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
